import { Url } from '@rossum/api-client';
import {
  SchemaAction,
  SchemaRule,
  SchemaSection,
} from '@rossum/api-client/schemas';
import { Stack } from '@rossum/ui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { isDeepEqual } from 'remeda';
import { ContentTitle } from '../../../ui/content-group/ContentTitle';
import { ContentWrapper } from '../../../ui/content-group/ContentWrapper';
import { EmptyState } from '../../../ui/empty-state/EmptyState';
import { FormulaPreviewGrid } from '../../formulas/FormulaPreview/FormulaPreviewGrid';
import { UpdateButton } from '../../formulas/FormulaPreview/UpdateButton';
import { FORMULA_PREVIEW_KEY, RULE_PREVIEW_KEY } from '../keys';
import { flattenDatapoints } from './flattenDatapoints';

type FormulaPreviewTileProps = {
  schemaContentDraft: SchemaSection[];
  schemaRulesDraft: Partial<SchemaRule>[];
  currentFormulaId: string;
  queueUrls: Url[];
  type: 'rule' | 'formula' | 'reasoning';
  renderAction?: ((action: SchemaAction) => React.ReactNode) | undefined;
};

const isInvalidSchema = (s: SchemaSection[]) =>
  flattenDatapoints(s).some(schema => !schema.label || !schema.id);

const isInvalidRule = (d: Partial<SchemaRule>[]) =>
  d.some(rule => !rule.name || !rule.triggerCondition);

export const FormulaPreviewTile = ({
  schemaContentDraft,
  schemaRulesDraft,
  currentFormulaId,
  queueUrls,
  type,
  renderAction,
}: FormulaPreviewTileProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [schemaForPreview, setSchemaForPreview] =
    useState<SchemaSection[]>(schemaContentDraft);

  const [ruleForPreview, setRuleForPreview] =
    useState<Partial<SchemaRule>[]>(schemaRulesDraft);

  const isUpToDate =
    isDeepEqual(schemaContentDraft, schemaForPreview) &&
    isDeepEqual(schemaRulesDraft, ruleForPreview);

  // This means that the next schema draft is not valid, therefore you need to make adjustments in the form
  const draftInvalid =
    isInvalidSchema(schemaContentDraft) || isInvalidRule(schemaRulesDraft);

  // This means that the schema in state is not valid (usually when creating new field / rule), so you cannot display preview until you update the state
  const previewInvalid =
    isInvalidSchema(schemaForPreview) || isInvalidRule(ruleForPreview);

  return (
    <ContentWrapper>
      <ContentTitle
        title={intl.formatMessage({
          id: `features.formulas.previewType.${type}.title`,
        })}
        description={intl.formatMessage({
          id: `features.formulas.previewType.${type}.description`,
        })}
      />

      <Stack spacing={2} pt={3}>
        {previewInvalid ? (
          <EmptyState
            title={intl.formatMessage({
              id: 'features.formulas.formulaFieldSection.emptyTitle',
            })}
            description={intl.formatMessage({
              id: `features.formulas.formulaFieldSection.${draftInvalid ? 'noSchemaId' : 'noFormula'}`,
            })}
          />
        ) : (
          <FormulaPreviewGrid
            queueUrls={queueUrls}
            type={type === 'rule' ? 'rule' : 'formula'}
            currentFormulaId={currentFormulaId}
            schemaRules={ruleForPreview}
            schemaForPreview={schemaForPreview}
            renderAction={renderAction}
          />
        )}
        <Stack alignItems="end">
          <UpdateButton
            updateValue={() => {
              setSchemaForPreview(schemaContentDraft);
              setRuleForPreview(schemaRulesDraft);

              queryClient.invalidateQueries({
                queryKey: [FORMULA_PREVIEW_KEY],
              });

              queryClient.invalidateQueries({
                queryKey: [RULE_PREVIEW_KEY],
              });
            }}
            isUpToDate={isUpToDate}
            disabled={draftInvalid}
          />
        </Stack>
      </Stack>
    </ContentWrapper>
  );
};
